import {
  call,
  CallEffect,
  debounce,
  put,
  PutEffect,
  select,
  SelectEffect,
  takeEvery,
  takeLatest
} from 'redux-saga/effects';

import { AUTH_SUCCESS, AuthSuccessAction } from 'js/actionCreators/authActions';
import { SET_RENDERER_USER, SetRendererUserAction } from 'js/actionCreators/rendererActions';
import { showError } from 'js/actionCreators/uiActions';
import { APP_DATA_KEYS } from 'js/config/consts';
import {
  deleteLogoRequested,
  deleteLogoSuccess,
  loadLogoSuccess,
  setLastSynced,
  updateOverlaySettings,
  uploadLogoFailure,
  uploadLogoRequested,
  uploadLogoSuccess
} from 'js/reducers/brandKitLogo';
import { appServices } from 'js/shared/helpers/app-services/AppServices';
import { BrandKitLogoState, HTTPError, RootState, VSCAssetLogoSourceName } from 'js/types';

type PersistedLogoState = Omit<BrandKitLogoState, 'uploadStatus' | 'uploadError' | 'lastSynced'>;

export const selectPersistedLogo = (state: RootState): PersistedLogoState => ({
  assetId: state.brandKitLogo.assetId,
  contentType: state.brandKitLogo.contentType,
  filename: state.brandKitLogo.filename,
  position: state.brandKitLogo.position,
  opacity: state.brandKitLogo.opacity,
  size: state.brandKitLogo.size,
  showOverlay: state.brandKitLogo.showOverlay
});

export function* syncLogo(): Generator<SelectEffect | CallEffect | PutEffect, void, PersistedLogoState> {
  try {
    const payload = yield select(selectPersistedLogo);
    yield call({ fn: appServices.setAppData, context: appServices }, APP_DATA_KEYS.BRAND_KIT_LOGO, payload);
    yield put(setLastSynced(Date.now()));
  } catch (error) {
    yield put(
      showError({
        message: 'Error syncing brand kit logo. Please try again later.',
        description: error instanceof Error ? error.message : undefined
      })
    );
    console.error('Error syncing brand kit logo:', error);
  }
}

export function* loadLogo(
  action: AuthSuccessAction | SetRendererUserAction
): Generator<CallEffect | PutEffect, void, PersistedLogoState> {
  const isRenderer = action.type === SET_RENDERER_USER;
  const fetchMethod = isRenderer ? appServices.getAppDataForRenderer : appServices.getAppData;
  try {
    const payload = yield call({ fn: fetchMethod, context: appServices }, APP_DATA_KEYS.BRAND_KIT_LOGO);
    yield put(loadLogoSuccess(payload));
  } catch (error) {
    const httpError = error as HTTPError;
    const statusCode = httpError?.httpStatusCode;

    // 404 is expected when the user hasn't uploaded a logo yet
    if (statusCode === 404 || statusCode === '404') {
      return;
    }

    console.error('Error loading brand kit logo:', error);
    yield put(
      showError({
        message: 'Error loading brand kit logo. Please try again later.',
        description: error instanceof Error ? String(error.message) : String(error)
      })
    );
  }
}

export function* handleUploadLogo(
  action: ReturnType<typeof uploadLogoRequested>
): Generator<SelectEffect | CallEffect | PutEffect, void, number | null> {
  try {
    const existingAssetId = yield select((state: RootState) => state.brandKitLogo.assetId);
    if (existingAssetId) {
      yield call({ fn: appServices.deleteAsset, context: appServices }, existingAssetId);
    }

    const { file, filename } = action.payload;
    const assetId = (yield call(
      { fn: appServices.uploadAsset, context: appServices },
      {
        file,
        filename,
        source: VSCAssetLogoSourceName.UPLOADED_LOGO
      }
    )) as number;

    yield put(uploadLogoSuccess({ assetId, contentType: file.type, filename }));
  } catch (error) {
    console.error('Error uploading brand kit logo:', error);
    yield put(uploadLogoFailure(error instanceof Error ? error.message : 'Upload failed'));
  }
}

export function* handleDeleteLogo(): Generator<SelectEffect | CallEffect | PutEffect, void, number | null> {
  try {
    const assetId = yield select((state: RootState) => state.brandKitLogo.assetId);
    if (assetId) {
      yield call({ fn: appServices.deleteAsset, context: appServices }, assetId);
    }
    yield put(deleteLogoSuccess());
  } catch (error) {
    console.error('Error deleting brand kit logo:', error);
    yield put(
      showError({
        message: 'Error removing brand kit logo. Please try again later.',
        description: error instanceof Error ? error.message : undefined
      })
    );
  }
}

export const LOGO_SYNC_DELAY = 2000;
export default function* brandKitLogoSaga() {
  yield debounce(
    LOGO_SYNC_DELAY,
    [uploadLogoSuccess.type, deleteLogoSuccess.type, updateOverlaySettings.type],
    syncLogo
  );
  yield takeLatest([AUTH_SUCCESS, SET_RENDERER_USER] as const, loadLogo);
  yield takeEvery(uploadLogoRequested.type, handleUploadLogo);
  yield takeEvery(deleteLogoRequested.type, handleDeleteLogo);
}
